import React, { useEffect, useState } from "react";
import api from "../../api";
import swal from "sweetalert";
import Select from "react-select";
import usePermission from "../../hooks/usePermission";

const MenuManager = () => {
 const { hasPermission } = usePermission();
 const canCreateMenu = hasPermission("create:menus");
 const canEditMenu = hasPermission("edit:menus");
 const canDeleteMenu = hasPermission("delete:menus");
 const [loading, setLoading] = useState(true);
 const [menus, setMenus] = useState([]);
 const [flatMenus, setFlatMenus] = useState([]);
 const [editingMenu, setEditingMenu] = useState(null);
 const [roles, setRoles] = useState([]);

 const [form, setForm] = useState({
  title: "",
  key: "",
  to: "",
  parent: "",
  icon: "",
  allowedRoles: [],
  requiredPermission: "",
  order: 1,
  isVisible: true,
 });

 const loadMenus = async () => {
 const res = await api.get("/menus?limit=999");
 const tree = res.data.data || [];

 const flatten = [];
 const recurse = (arr) => {
 arr.forEach((item) => {
 flatten.push(item);
 if (item.children?.length) recurse(item.children);
 });
 };
 recurse(tree);

 setMenus(tree);
 setFlatMenus(flatten);
 };

 const loadRoles = async () => {
 const res = await api.get("/roles?limit=999");
 setRoles(res.data.data || []);
 };

 useEffect(() => {
  const loadData = async () => {
   try {
    setLoading(true);
    await Promise.all([loadMenus(), loadRoles()]);
   } catch (err) {
    swal("Error", "Failed to load data", "error");
   } finally {
    setLoading(false);
   }
  };
  loadData();
 }, []);

 const handleSubmit = async () => {
  if (!form.title || !form.key) {
   return swal("Error", "Title & Key are required!", "error");
  }

  try {
   await api.post("/menus", { ...form, parent: form.parent || null });
   swal("Success", "Menu added successfully", "success");
   setForm({
    title: "", key: "", to: "", parent: "", icon: "",
    allowedRoles: [], requiredPermission: "", order: 1, isVisible: true,
   });
   await loadMenus();
  } catch (err) {
   swal("Error", "Failed to add menu", "error");
  }
 };

 const handleToggleVisibility = async (menu) => {
  try {
   await api.patch(`/menus/${menu._id}/toggle`);
   await loadMenus();
  } catch (err) {
   swal("Error", "Failed to toggle visibility", "error");
  }
 };

 const handleDelete = async (id) => {
 swal({
 title: "Delete Menu?",
 text: "This action cannot be undone",
 icon: "warning",
 buttons: ["Cancel", "Delete"],
 dangerMode: true,
 }).then(async (yes) => {
 if (!yes) return;
 try {
 await api.delete(`/menus/${id}`);
 swal("Deleted", "Menu removed", "success");
 await loadMenus();
 } catch (err) {
 swal("Error", "Failed to delete menu", "error");
 }
 });
 };

 const handleUpdate = async () => {
 try {
 await api.put(`/menus/${editingMenu._id}`, editingMenu);
 swal("Updated", "Menu updated successfully", "success");
 setEditingMenu(null);
 await loadMenus();
 } catch (err) {
 swal("Error", "Failed to update menu", "error");
 }
 };

 const renderTree = (nodes) => {
  return nodes.map((m) => (
   <div key={m._id} className="mb-2">
    <div className={`d-flex justify-content-between align-items-center p-3 border rounded shadow-xs ${m.isVisible === false ? 'bg-light opacity-75' : 'bg-white'}`}>
     <div className="d-flex align-items-center flex-wrap gap-2 flex-grow-1">
      <div className="d-flex align-items-center me-3" style={{ minWidth: "220px" }}>
       {m.icon ? (
        <i className={`${m.icon} fa-fw me-2 text-primary fs-5`}></i>
       ) : (
        <i className="fa fa-folder-open fa-fw me-2 text-muted fs-5"></i>
       )}
       <span className="font-w600 text-dark">{m.title}</span>
      </div>

      {/* Route badge */}
      {m.to && (
       <span className="badge bg-light text-primary border rounded-pill px-2 py-1 small me-2">
        {m.to}
       </span>
      )}

      <span className="text-muted small">({m.key})</span>

      {/* Required permission badge */}
      {m.requiredPermission && (
       <span className="badge bg-warning text-dark rounded-pill px-2 py-1 small" title="Required Permission">
        <i className="fa fa-key me-1"></i>{m.requiredPermission}
       </span>
      )}

      {/* Allowed roles badges */}
      {m.allowedRoles?.length > 0 && (
       <div className="d-flex flex-wrap gap-1 ms-1">
        {m.allowedRoles.map((r) => (
         <span key={r} className="badge bg-secondary rounded-pill">{r}</span>
        ))}
       </div>
      )}

      {/* Visibility badge */}
      <span className={`badge rounded-pill ms-1 ${m.isVisible === false ? 'bg-danger' : 'bg-success'}`}>
       {m.isVisible === false ? 'Hidden' : 'Visible'}
      </span>
     </div>

     <div className="d-flex gap-2 ms-3">
      {/* Toggle visibility */}
      {canEditMenu && (
       <button
        className={`btn btn-sm border-0 ${m.isVisible === false ? 'btn-outline-success' : 'btn-outline-secondary'}`}
        onClick={() => handleToggleVisibility(m)}
        title={m.isVisible === false ? 'Show Menu' : 'Hide Menu'}
       >
        <i className={`fa ${m.isVisible === false ? 'fa-eye' : 'fa-eye-slash'}`}></i>
       </button>
      )}
      {canEditMenu && (
       <button
        className="btn btn-sm btn-outline-primary border-0"
        onClick={() => setEditingMenu(m)}
        title="Edit Menu"
       >
        <i className="fa fa-pen"></i>
       </button>
      )}
      {canDeleteMenu && (
       <button
        className="btn btn-sm btn-outline-danger border-0"
        onClick={() => handleDelete(m._id)}
        title="Delete Menu"
       >
        <i className="fa fa-trash"></i>
       </button>
      )}
     </div>
    </div>

    {m.children?.length > 0 && (
     <div className="ms-4 mt-2 border-start ps-3" style={{ borderLeft: "2px dashed #ddd" }}>
      {renderTree(m.children)}
     </div>
    )}
   </div>
  ));
 };

 return (
 <div className="container-fluid py-4">
 <h3 className="fw-bold mb-4 text-center text-lg-start">Menu Manager</h3>

 {loading ? (
 <div className="text-center py-5">Loading...</div>
 ) : (
 <div className="row g-3">

 {/* Form */}
 <div className="col-12 col-lg-4">
 <div className="card shadow-sm p-3 h-100">
 <h5 className="fw-semibold mb-3">Create Menu</h5>

 <div className="mb-2">
 <label className="form-label">Title*</label>
 <input
 className="form-control"
 value={form.title}
 placeholder="Dashboard"
 onChange={(e) =>
 setForm({
 ...form,
 title: e.target.value,
 key: e.target.value.toLowerCase().replace(/\s+/g, "-"),
 })
 }
 />
 </div>

 <div className="mb-2">
 <label className="form-label">Route</label>
 <input
 className="form-control"
 value={form.to}
 placeholder="/dashboard"
 onChange={(e) => setForm({ ...form, to: e.target.value })}
 />
 </div>

 <div className="mb-2">
 <label className="form-label">Parent</label>
 <select
 className="form-select"
 value={form.parent}
 onChange={(e) => setForm({ ...form, parent: e.target.value })}
 >
 <option value="">None (Top Level)</option>
 {flatMenus
 .filter((m) => !m.parent)
 .map((m) => (
 <option key={m._id} value={m.key}>
 {m.title}
 </option>
 ))}
 </select>
 </div>

 <div className="mb-2">
 <label className="form-label">Icon</label>
 <input
 className="form-control"
 value={form.icon}
 placeholder="fa fa-home"
 onChange={(e) => setForm({ ...form, icon: e.target.value })}
 />
 </div>

  <div className="mb-2">
   <label className="form-label">Allowed Roles</label>
   <Select
    isMulti
    options={roles.map((r) => ({ value: r.name, label: r.name }))}
    value={form.allowedRoles?.map((r) => ({ value: r, label: r })) || []}
    onChange={(selected) =>
     setForm({ ...form, allowedRoles: selected ? selected.map((o) => o.value) : [] })
    }
    className="react-select-container"
    classNamePrefix="react-select"
   />
  </div>

  <div className="mb-2">
   <label className="form-label">Required Permission <span className="text-muted small">(optional)</span></label>
   <input
    className="form-control"
    value={form.requiredPermission}
    placeholder="e.g. read:billings"
    onChange={(e) => setForm({ ...form, requiredPermission: e.target.value })}
   />
   <small className="text-muted">User must have this permission to see the menu</small>
  </div>

  <div className="mb-2">
   <label className="form-label">Order</label>
   <input
    type="number"
    className="form-control"
    value={form.order}
    onChange={(e) => setForm({ ...form, order: Number(e.target.value) })}
   />
  </div>

  <div className="mb-3 d-flex align-items-center gap-2">
   <div className="form-check form-switch mb-0">
    <input
     className="form-check-input"
     type="checkbox"
     role="switch"
     id="createIsVisible"
     checked={form.isVisible}
     onChange={(e) => setForm({ ...form, isVisible: e.target.checked })}
    />
    <label className="form-check-label" htmlFor="createIsVisible">
     Visible in Sidebar
    </label>
   </div>
  </div>

  {canCreateMenu && (
   <button className="btn btn-primary mt-2 w-100" onClick={handleSubmit}>
    <i className="fa fa-save me-2"></i> Save Menu
   </button>
  )}
  </div>
 </div>

 {/* Tree */}
 <div className="col-12 col-lg-8">
 <div className="card shadow-sm p-3 h-100">
 <h5 className="fw-semibold mb-3">Menu Structure</h5>
 
 <div style={{ maxHeight: "70vh", overflowY: "auto" }}>
 {menus.length > 0 ? renderTree(menus) : (
 <p className="text-muted">No menus found.</p>
 )}
 </div>
 </div>
 </div>
 </div>
 )}

 {/* Edit Modal */}
 {editingMenu && (
 <div
 className="modal fade show"
 style={{ display: "block", background: "rgba(0,0,0,0.5)" }}
 >
 <div className="modal-dialog modal-dialog-centered modal-lg modal-fullscreen-sm-down">
 <div className="modal-content">
 <div className="modal-header">
 <h5>Edit Menu</h5>
 <button className="btn-close" onClick={() => setEditingMenu(null)} />
 </div>

 <div className="modal-body">
 <div className="row g-3">
 <div className="col-md-6">
 <label className="form-label">Title</label>
 <input
 className="form-control"
 value={editingMenu.title}
 onChange={(e) =>
 setEditingMenu({ ...editingMenu, title: e.target.value })
 }
 />
 </div>

 <div className="col-md-6">
 <label className="form-label">Route</label>
 <input
 className="form-control"
 value={editingMenu.to}
 onChange={(e) =>
 setEditingMenu({ ...editingMenu, to: e.target.value })
 }
 />
 </div>

 <div className="col-md-6">
 <label className="form-label">Parent</label>
 <select
 className="form-select"
 value={editingMenu.parent || ""}
 onChange={(e) =>
 setEditingMenu({ ...editingMenu, parent: e.target.value })
 }
 >
 <option value="">None (Top Level)</option>
 {flatMenus
 .filter((m) => m._id !== editingMenu._id)
 .map((m) => (
 <option key={m._id} value={m.key}>
 {m.title}
 </option>
 ))}
 </select>
 </div>

 <div className="col-md-6">
 <label className="form-label">Icon</label>
 <input
 className="form-control"
 value={editingMenu.icon || ""}
 onChange={(e) =>
 setEditingMenu({ ...editingMenu, icon: e.target.value })
 }
 />
 </div>

  <div className="col-md-6">
   <label className="form-label">Allowed Roles</label>
   <Select
    isMulti
    options={roles.map((r) => ({ value: r.name, label: r.name }))}
    value={editingMenu.allowedRoles?.map((r) => ({ value: r, label: r })) || []}
    onChange={(selected) =>
     setEditingMenu({ ...editingMenu, allowedRoles: selected ? selected.map((o) => o.value) : [] })
    }
    className="react-select-container"
    classNamePrefix="react-select"
   />
  </div>

  <div className="col-md-6">
   <label className="form-label">Required Permission <span className="text-muted small">(optional)</span></label>
   <input
    className="form-control"
    value={editingMenu.requiredPermission || ""}
    placeholder="e.g. read:billings"
    onChange={(e) => setEditingMenu({ ...editingMenu, requiredPermission: e.target.value })}
   />
  </div>

  <div className="col-md-6">
   <label className="form-label">Order</label>
   <input
    className="form-control"
    type="number"
    value={editingMenu.order}
    onChange={(e) => setEditingMenu({ ...editingMenu, order: Number(e.target.value) })}
   />
  </div>

  <div className="col-md-6 d-flex align-items-end pb-1">
   <div className="form-check form-switch">
    <input
     className="form-check-input"
     type="checkbox"
     role="switch"
     id="editIsVisible"
     checked={editingMenu.isVisible !== false}
     onChange={(e) => setEditingMenu({ ...editingMenu, isVisible: e.target.checked })}
    />
    <label className="form-check-label" htmlFor="editIsVisible">
     Visible in Sidebar
    </label>
   </div>
  </div>
  </div>
  </div>

 <div className="modal-footer">
 <button className="btn btn-secondary" onClick={() => setEditingMenu(null)}>
 Close
 </button>
 <button className="btn btn-success" onClick={handleUpdate}>
 <i className="fa fa-save me-2"></i> Save Changes
 </button>
 </div>
 </div>
 </div>
 </div>
 )}
 </div>
 );
};

export default MenuManager;
